import { StarFilledIcon } from "@radix-ui/react-icons";
import { useMutation } from "@tanstack/react-query";
import { StarIcon } from "lucide-react";

import { Spinner } from "@/components/Spinner";
import { Button, ButtonProps } from "@/components/ui/button";
import { MenuItem } from "@/components/ui/menu";
import {
  CustomNodeHistoryItem,
  favoriteCustomNode,
  unfavoriteCustomNode,
} from "@/lib/api";
import { cn } from "@/lib/utils";

export function FavoriteCustomNodeButton({
  node,
  size = "icon",
  variant = "outline",
}: {
  node: CustomNodeHistoryItem;
  variant?: ButtonProps["variant"];
  size?: Extract<ButtonProps["size"], "smIcon" | "icon">;
}) {
  const favoriteMutation = useMutation({
    mutationFn: () => favoriteCustomNode(node.id),
  });
  const unfavoriteMutation = useMutation({
    mutationFn: () => unfavoriteCustomNode(node.id),
  });

  return (
    <>
      {node.favorited ? (
        <Button
          variant={variant}
          size={size}
          onClick={() => unfavoriteMutation.mutate()}
        >
          <StarFilledIcon className="text-yellow-10 dark:text-yellowdark-10" />
        </Button>
      ) : (
        <Button
          variant={variant}
          size={size}
          onClick={() => favoriteMutation.mutate()}
        >
          <StarIcon />
        </Button>
      )}
    </>
  );
}

export function FavoriteCustomNodeMenuItem({
  node,
}: {
  node: CustomNodeHistoryItem;
}) {
  const favoriteMutation = useMutation({
    mutationFn: () => favoriteCustomNode(node.id),
  });
  const unfavoriteMutation = useMutation({
    mutationFn: () => unfavoriteCustomNode(node.id),
  });

  return (
    <MenuItem
      disabled={unfavoriteMutation.isPending || favoriteMutation.isPending}
      onSelect={(e) => {
        e.preventDefault();

        if (node.favorited) {
          unfavoriteMutation.mutate();
        } else {
          favoriteMutation.mutate();
        }
      }}
    >
      {unfavoriteMutation.isPending || favoriteMutation.isPending ? (
        <Spinner />
      ) : node.favorited ? (
        <StarFilledIcon className="text-slate-dim" />
      ) : (
        <StarIcon className="text-slate-dim" />
      )}
      {node.favorited ? "Unfavorite" : "Favorite"}
    </MenuItem>
  );
}

export function FavoriteCustomNodeIcon({
  node,
  className,
}: {
  node: CustomNodeHistoryItem;
  className?: string;
}) {
  return (
    <>
      {node.favorited ? (
        <StarFilledIcon className={cn(className, "text-slatea-dim")} />
      ) : null}
    </>
  );
}
